import { useNavigate, useParams } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';
import { Typography } from '@la/ds-ui-components';
import { Product } from '@la/types';
import { formatDateRange } from '@la/utilities';
import useMediaQuery from 'lib/hooks/useMediaQuery';
import { breakpointQueries } from 'lib/media-queries/breakpoints';
import { getLAHostnameParts } from 'lib/utils/urlUtils';
import {
  resetRegistrationAddOns,
  setRegistrationAddOns,
  setTaxRate,
} from 'redux/registrationAddOnsSlice';
import { useGetTournamentQuery } from 'redux/services/tournamentApi';
import { useAppDispatch } from 'redux/store';
import MainContent from 'domains/Shell/MainContent/MainContent';
import { TOURNAMENT_NOT_FOUND_ERROR_MESSAGE } from '../utils/errorMessages';
import { AboutSection } from './AboutSection/AboutSection';
import { DetailsCard } from './DetailsCard/DetailsCard';
import { FeesCard } from './FeesCard/FeesCard';
import { LocationCard } from './LocationCard/LocationCard';
import { RegistrationWindowCard } from './RegistrationWindowCard/RegistrationWindowCard';
import styles from './TournamentDetailsPage.module.scss';
import { TournamentFeesSidebar } from './TournamentFeesSidebar/TournamentFeesSidebar';
import { PageTitleSection } from './components/PageTitleSection/PageTitleSection';
import { RegisterSection } from './components/RegisterSection/RegisterSection';
import { SecondaryNavigationBar } from './components/SecondaryNavigationBar/SecondaryNavigationBar';
import { getRegistrationPeriodStatus } from './utils/registration';

export type RegistrationPeriodStatus =
  | {
      state: 'before-registration-period-opens';
      numberOfDays: number;
    }
  | {
      state: 'before-registration-period-ends';
      numberOfDays?: number;
    }
  | {
      state: 'after-registration-period-opens';
    }
  | {
      state: 'after-registration-period-ends';
    }
  | {
      // Used when there are divisions but there is no start or end
      // date for registration (open registration)
      state: 'no-registration-dates';
    }
  | {
      state: 'no-divisions';
    };

export function TournamentDetailsPage() {
  const dispatch = useAppDispatch();
  const { playersAndStaffFees, showUpdatedECommerceInRegFlowContent } =
    useFlags();
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const isTabletLandscapeUp = useMediaQuery(
    breakpointQueries.tabletLandscapeUp
  );

  if (!tournamentId) {
    throw new Error('Tournament ID is not defined');
  }

  const { subdomain } = getLAHostnameParts();
  const { data: tournamentData, isLoading: isTournamentLoading } =
    useGetTournamentQuery(
      {
        siteDomain: subdomain,
        tournamentId: tournamentId,
      },
      { skip: !subdomain || !tournamentId }
    );

  if (isTournamentLoading) {
    return <MainContent />;
  }

  if (!tournamentData) {
    throw new Error(TOURNAMENT_NOT_FOUND_ERROR_MESSAGE);
  }

  const {
    aboutHTML,
    divisions,
    endDate,
    experienceLevel,
    gender,
    hideLocationDetails,
    hotelLinks,
    location,
    logoURL,
    name,
    registrationAddOns,
    registrationEndDate,
    registrationStartDate,
    salesTax,
    season,
    sport,
    startDate,
    timeZone,
  } = tournamentData;

  if (showUpdatedECommerceInRegFlowContent) {
    dispatch(resetRegistrationAddOns());
    if (registrationAddOns) {
      dispatch(setRegistrationAddOns(registrationAddOns as Product[]));
    }
    if (salesTax) {
      dispatch(setTaxRate(salesTax));
    }
  }

  const selectRegistrationTypeLink = `/app/tournaments/${tournamentId}/select-registration-type`;
  const teamRegistrationLink = `/app/tournaments/${tournamentId}/registration`;

  const registrationLink = playersAndStaffFees
    ? selectRegistrationTypeLink
    : teamRegistrationLink;

  const locationLink = location ? `/location/${location.id}` : undefined;
  const ageGroups: number[] = divisions
    .map((division) => division.ageGroup)
    .filter((ageGroup): ageGroup is number => ageGroup !== undefined);

  const navigateToSelectRegistrationType = () => {
    navigate(registrationLink);
  };

  const noDivisionsRegistrationPeriodState: RegistrationPeriodStatus = {
    state: 'no-divisions',
  };
  const registrationPeriodStatus = divisions.length
    ? getRegistrationPeriodStatus(
        registrationStartDate,
        registrationEndDate,
        timeZone
      )
    : noDivisionsRegistrationPeriodState;

  const isRegisterLinkShowing = [
    'before-registration-period-ends',
    'no-registration-dates',
  ].includes(registrationPeriodStatus.state);

  const registerSection = (
    <RegisterSection
      navigateToRegistration={navigateToSelectRegistrationType}
      registrationLink={registrationLink}
      registrationPeriodStatus={registrationPeriodStatus}
    />
  );

  return (
    <>
      <MainContent>
        {/* TODO: Use data from API endpoint */}
        <PageTitleSection
          logoUrl={logoURL}
          tournamentName={name}
          sport={sport}
        />
        <SecondaryNavigationBar
          hasVisibleTeams={divisions.some((division) => division.shouldDisplay)}
          registerSection={isTabletLandscapeUp ? registerSection : null}
          tournamentId={tournamentId}
        />
        <div className={styles.tournamentDetailsPage}>
          <div className={styles.header}>
            <Typography size="large" variant="headline">
              About {name}
            </Typography>
            <Typography size="xl" variant="ui" weight="bold">
              {formatDateRange(
                DateTime.fromISO(startDate),
                endDate ? DateTime.fromISO(endDate) : undefined
              )}
            </Typography>
          </div>
          <div className={styles.mainContent}>
            <RegistrationWindowCard
              endDate={registrationEndDate}
              hasRegistrationWindow={
                registrationPeriodStatus.state !== 'no-divisions'
              }
              href={registrationLink}
              showRegisterButton={isRegisterLinkShowing}
              startDate={registrationStartDate}
            />
            {!hideLocationDetails ? (
              <LocationCard href={locationLink} name={location?.name} />
            ) : null}
            <DetailsCard
              ageGroups={ageGroups}
              gender={gender}
              hotelLink={hotelLinks}
              level={experienceLevel}
              season={season}
              sport={sport}
            />
            {aboutHTML ? <AboutSection description={aboutHTML} /> : null}
          </div>
          {showUpdatedECommerceInRegFlowContent ? (
            <TournamentFeesSidebar tournamentData={tournamentData} />
          ) : (
            <FeesCard tournamentData={tournamentData} />
          )}
        </div>
      </MainContent>
      {!isTabletLandscapeUp ? (
        <div className={styles.registerSectionContainer}>{registerSection}</div>
      ) : null}
    </>
  );
}
